import React from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  CardActions,
  Stack,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { formatDateToYYYYMMDD, TimelineStatus } from "../../../../Utils/constants";
import { styled, alpha, useTheme } from '@mui/material/styles';

// Styled Components for better visual hierarchy
const TimelineCard = styled(Card)(({ theme, iscompleted }) => ({
  marginBottom: theme.spacing(1.5),
  borderLeft: `4px solid ${iscompleted === 'true' ? theme.palette.success.main : theme.palette.primary.main}`,
  opacity: iscompleted === 'true' ? 0.7 : 1,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[4],
  }
}));

const CompletionButton = styled(Button)(({ theme, iscompleted }) => ({
  minWidth: 120,
  fontWeight: 600,
  ...(iscompleted === 'true' && {
    backgroundColor: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    }
  })
}));

// Single timeline item card
const TimelineEntry = ({ event, onEditField, canEdit, onMarkComplete }) => {
  const theme = useTheme();
  const isCompleted = event.status === "complete";
  const isDisabled = event.ogStatus === "complete" || !canEdit;

  const handleToggleComplete = () => {
    if (isCompleted) {
      // If already completed, just toggle back without confirmation
      onEditField(
        event.index,
        "status",
        TimelineStatus[1]
      );
    } else {
      // If marking as complete, show confirmation and auto-save
      onMarkComplete(event);
    }
  };

  return (
    <TimelineCard
      variant="outlined"
      iscompleted={isCompleted ? 'true' : 'false'}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Typography
            variant="h6"
            sx={{
              textDecoration: isCompleted ? "line-through" : "none",
              color: isCompleted ? "text.secondary" : "text.primary",
              fontWeight: 600,
              flex: 1,
              fontSize: '1rem'
            }}
          >
            {event.objective}
          </Typography>
          
          <Chip
            icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
            label={formatDateToYYYYMMDD(event.projected_date)}
            size="small"
            variant="outlined"
            color={isCompleted ? "success" : "primary"}
            sx={{ ml: 1, fontWeight: 500 }}
          />
        </Box>

        {event.notes && (
          <Box 
            sx={{ 
              mt: 1,
              p: 1.5,
              bgcolor: alpha(theme.palette.grey[100], 0.5),
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
            >
              Notes:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
              {event.notes}
            </Typography>
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2, pt: 0 }}>
        <CompletionButton
          variant={isCompleted ? "contained" : "outlined"}
          color={isCompleted ? "success" : "primary"}
          onClick={handleToggleComplete}
          disabled={isDisabled}
          startIcon={isCompleted ? <CheckCircleIcon /> : <CheckCircleOutlineIcon />}
          size="small"
          iscompleted={isCompleted ? 'true' : 'false'}
        >
          {isCompleted ? "Completed" : "Mark Complete"}
        </CompletionButton>
      </CardActions>
    </TimelineCard>
  );
};

const CreatorTimeline = ({
  timelineEvents,
  onEditField,
  onSaveChanges,
  isSavingChanges,
  canEdit,
  openAlert,
  closeDialog,
}) => {
  const theme = useTheme();

  const handleMarkComplete = (event) => {
    // Show confirmation dialog
    openAlert(
      "Confirm Completion",
      `Are you sure you want to mark "${event.objective}" as complete? This will notify the brand and save your changes.`,
      () => {
        // On confirmation, mark as complete and auto-save
        closeDialog();
        onEditField(event.index, "status", "complete");
        // Trigger save automatically after a short delay to ensure state updates
        setTimeout(() => {
          onSaveChanges();
        }, 100);
      },
      closeDialog,
      "Confirm",
      "Cancel"
    );
  };

  if (!timelineEvents || timelineEvents.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          No timeline events to display
        </Typography>
      </Box>
    );
  }

  // Sort by date
  const currentTimeline = timelineEvents
    .map((event, index) => ({ ...event, index }))
    .sort((a, b) => new Date(a.projected_date) - new Date(b.projected_date));

  const completedCount = currentTimeline.filter(e => e.status === 'complete').length;
  const totalCount = currentTimeline.length;
  const progressPercentage = (completedCount / totalCount) * 100;

  return (
    <Box>
      {/* Timeline Header with Progress */}
      <Box sx={{ 
        mb: 2, 
        p: 2, 
        bgcolor: alpha(theme.palette.primary.main, 0.05), 
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Campaign Timeline
          </Typography>
          <Chip
            label={`${completedCount}/${totalCount} Complete`}
            size="small"
            color={progressPercentage === 100 ? "success" : "primary"}
            sx={{ fontWeight: 600 }}
          />
        </Box>

        <Box sx={{ 
          height: 6, 
          bgcolor: alpha(theme.palette.grey[300], 0.3), 
          borderRadius: 3,
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            height: '100%', 
            width: `${progressPercentage}%`,
            bgcolor: progressPercentage === 100 ? theme.palette.success.main : theme.palette.primary.main,
            transition: 'width 0.3s ease',
            borderRadius: 3,
          }} />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {Math.round(progressPercentage)}% of timeline objectives completed
        </Typography>
      </Box>

      {/* Timeline Items */}
      <Stack spacing={0}>
        {currentTimeline.map((event) => (
          <TimelineEntry 
            key={event.id} 
            event={event} 
            onEditField={onEditField}
            canEdit={canEdit}
            onMarkComplete={handleMarkComplete}
          />
        ))}
      </Stack>

      {/* Removed manual save button - saving happens automatically on completion confirmation */}
    </Box>
  );
};

export default CreatorTimeline;